// pages/strategic-playbook/tools/position-sizer.js
// ════════════════════════════════════════════════════
// POSITION SIZER — Kelly Criterion + Conviction Tier
// ════════════════════════════════════════════════════
// Calculates the right position size based on:
//   - Win probability + win/loss ratio (Kelly)
//   - Conviction tier (Probe / Position / Real Bet / All-in)
//   - Account size + max loss tolerance
//
// Half-Kelly is applied by default (Kelly assumes accurate
// probability estimates; few people have those).
// Conviction-tier cap further constrains output.
//
// Connects to: Buffett (circle of competence) + Ackman
// (concentrated conviction)
// ════════════════════════════════════════════════════

import { useState, useMemo } from 'react';
import Head from 'next/head';
import Link from 'next/link';

const CONVICTION_TIERS = [
  {
    id: 'probe',
    label: 'Probe',
    pct: 2,
    description: 'I think this might work — let\'s see. Modest evidence, exploratory.',
  },
  {
    id: 'position',
    label: 'Position',
    pct: 5,
    description: 'I have a thesis with modest evidence. Worth a real allocation.',
  },
  {
    id: 'real-bet',
    label: 'Real Bet',
    pct: 12,
    description: 'Done the work. High confidence. Multiple confirmations.',
  },
  {
    id: 'all-in',
    label: 'All-in Conviction',
    pct: 25,
    description: 'Once or twice in a decade. Druckenmiller pound short level. Earned, not declared.',
  },
];

export default function PositionSizer() {
  const [accountSize, setAccountSize] = useState(100000);
  const [winProb, setWinProb] = useState(60);
  const [winPct, setWinPct] = useState(20);
  const [lossPct, setLossPct] = useState(10);
  const [convictionTier, setConvictionTier] = useState('position');
  const [kellyFraction, setKellyFraction] = useState(0.5); // half-Kelly default

  const result = useMemo(() => {
    return calculateSize({
      accountSize,
      winProb: winProb / 100,
      winPct: winPct / 100,
      lossPct: lossPct / 100,
      convictionTier,
      kellyFraction,
    });
  }, [accountSize, winProb, winPct, lossPct, convictionTier, kellyFraction]);

  return (
    <div className="sp-root">
      <Head>
        <title>Position Sizer · Strategic Intelligence</title>
      </Head>

      <div className="sp-page">
        <ToolTopbar />

        <div style={{ marginBottom: 32 }}>
          <Link
            href="/strategic-playbook/tools"
            className="sp-reader-back"
            style={{ marginBottom: 16 }}
          >
            ← Tools
          </Link>
          <div
            style={{
              fontFamily: 'var(--sp-font-mono)',
              fontSize: 10,
              textTransform: 'uppercase',
              letterSpacing: '0.20em',
              color: 'var(--sp-aged-brass)',
              marginBottom: 14,
            }}
          >
            Decision Sizing · Calculator
          </div>
          <h1
            style={{
              fontFamily: 'var(--sp-font-serif)',
              fontWeight: 500,
              fontSize: 32,
              lineHeight: 1.2,
              color: 'var(--sp-warm-cream)',
              margin: '0 0 14px 0',
              letterSpacing: '-0.01em',
            }}
          >
            Position Sizer
          </h1>
          <p
            style={{
              fontSize: 14,
              lineHeight: 1.65,
              color: 'rgba(232, 221, 196, 0.78)',
              maxWidth: 700,
              margin: 0,
            }}
          >
            Kelly Criterion + Conviction Tier sizing. Half-Kelly default (Kelly assumes
            accurate probability — few have it). Conviction tier caps the result.
            Connects to Buffett (circle of competence) + Ackman (concentrated conviction).
          </p>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 24,
          }}
          className="position-sizer-grid"
        >
          {/* INPUTS */}
          <div
            style={{
              padding: '28px 28px',
              background: 'var(--sp-soft-charcoal)',
              border: '1px solid var(--sp-divider)',
              borderRadius: 'var(--sp-radius-card)',
            }}
          >
            <h3
              style={{
                fontFamily: 'var(--sp-font-mono)',
                fontSize: 10,
                textTransform: 'uppercase',
                letterSpacing: '0.18em',
                color: 'var(--sp-aged-brass)',
                margin: '0 0 22px 0',
              }}
            >
              Inputs
            </h3>

            <Field label="Account size" suffix="$">
              <NumberInput
                value={accountSize}
                onChange={setAccountSize}
                step={1000}
                min={0}
              />
            </Field>

            <Field
              label="Win probability"
              suffix="%"
              hint="Be honest. Most overestimate."
            >
              <NumberInput
                value={winProb}
                onChange={setWinProb}
                step={1}
                min={0}
                max={100}
              />
            </Field>

            <Field label="Win amount" suffix="% gain">
              <NumberInput
                value={winPct}
                onChange={setWinPct}
                step={1}
                min={0}
              />
            </Field>

            <Field label="Loss amount" suffix="% loss">
              <NumberInput
                value={lossPct}
                onChange={setLossPct}
                step={1}
                min={0}
              />
            </Field>

            <Field label="Kelly fraction" hint="Half-Kelly is the standard. Kelly assumes you know probabilities precisely.">
              <select
                value={kellyFraction}
                onChange={(e) => setKellyFraction(parseFloat(e.target.value))}
                style={inputStyle}
              >
                <option value={0.25}>Quarter-Kelly (most conservative)</option>
                <option value={0.5}>Half-Kelly (recommended)</option>
                <option value={0.75}>Three-quarter-Kelly</option>
                <option value={1.0}>Full Kelly (only if probability is certain)</option>
              </select>
            </Field>

            <div style={{ marginTop: 24 }}>
              <div
                style={{
                  fontFamily: 'var(--sp-font-mono)',
                  fontSize: 10,
                  textTransform: 'uppercase',
                  letterSpacing: '0.14em',
                  color: 'rgba(232, 221, 196, 0.55)',
                  marginBottom: 10,
                }}
              >
                Conviction Tier
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {CONVICTION_TIERS.map((tier) => (
                  <button
                    key={tier.id}
                    onClick={() => setConvictionTier(tier.id)}
                    style={{
                      textAlign: 'left',
                      padding: '12px 14px',
                      background:
                        convictionTier === tier.id
                          ? 'var(--sp-brass-bg)'
                          : 'transparent',
                      border: `1px solid ${
                        convictionTier === tier.id
                          ? 'var(--sp-aged-brass)'
                          : 'var(--sp-divider)'
                      }`,
                      borderRadius: 'var(--sp-radius)',
                      cursor: 'pointer',
                      transition: 'border-color 0.2s, background 0.2s',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: 4,
                      }}
                    >
                      <span
                        style={{
                          fontFamily: 'var(--sp-font-serif)',
                          fontSize: 14,
                          color:
                            convictionTier === tier.id
                              ? 'var(--sp-warm-cream)'
                              : 'rgba(232, 221, 196, 0.85)',
                        }}
                      >
                        {tier.label}
                      </span>
                      <span
                        style={{
                          fontFamily: 'var(--sp-font-mono)',
                          fontSize: 11,
                          color:
                            convictionTier === tier.id
                              ? 'var(--sp-aged-brass)'
                              : 'rgba(232, 221, 196, 0.50)',
                        }}
                      >
                        max {tier.pct}% of account
                      </span>
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        lineHeight: 1.5,
                        color: 'rgba(232, 221, 196, 0.60)',
                      }}
                    >
                      {tier.description}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* OUTPUTS */}
          <div>
            {/* Recommended Size — large, brass */}
            <div
              style={{
                padding: '28px',
                background:
                  'linear-gradient(135deg, var(--sp-card-charcoal) 0%, var(--sp-soft-charcoal) 100%)',
                border: '1px solid var(--sp-aged-brass)',
                borderRadius: 'var(--sp-radius-card)',
                marginBottom: 16,
              }}
            >
              <div
                style={{
                  fontFamily: 'var(--sp-font-mono)',
                  fontSize: 10,
                  textTransform: 'uppercase',
                  letterSpacing: '0.18em',
                  color: 'var(--sp-aged-brass)',
                  marginBottom: 14,
                }}
              >
                Recommended Size
              </div>
              <div
                style={{
                  fontFamily: 'var(--sp-font-serif)',
                  fontSize: 42,
                  lineHeight: 1.1,
                  color: 'var(--sp-warm-cream)',
                  marginBottom: 8,
                }}
              >
                ${result.recommendedSize.toLocaleString()}
              </div>
              <div
                style={{
                  fontFamily: 'var(--sp-font-mono)',
                  fontSize: 13,
                  color: 'var(--sp-aged-brass)',
                  marginBottom: 18,
                  letterSpacing: '0.04em',
                }}
              >
                {result.recommendedPct.toFixed(2)}% of account
              </div>
              <div
                style={{
                  fontFamily: 'var(--sp-font-serif)',
                  fontStyle: 'italic',
                  fontSize: 14,
                  lineHeight: 1.55,
                  color: 'var(--sp-warm-cream)',
                  borderLeft: '2px solid var(--sp-aged-brass)',
                  paddingLeft: 14,
                }}
              >
                {result.verdict}
              </div>
            </div>

            {/* Math breakdown */}
            <div
              style={{
                padding: '24px 28px',
                background: 'var(--sp-soft-charcoal)',
                border: '1px solid var(--sp-divider)',
                borderRadius: 'var(--sp-radius-card)',
                marginBottom: 16,
              }}
            >
              <h3
                style={{
                  fontFamily: 'var(--sp-font-mono)',
                  fontSize: 10,
                  textTransform: 'uppercase',
                  letterSpacing: '0.18em',
                  color: 'var(--sp-aged-brass)',
                  margin: '0 0 18px 0',
                }}
              >
                The Math
              </h3>
              <ResultRow label="Expected value per dollar" value={result.evPerDollar} />
              <ResultRow label="Full Kelly %" value={result.kellyPct.toFixed(2) + '%'} />
              <ResultRow
                label="Fractional Kelly applied"
                value={result.fractionalKellyPct.toFixed(2) + '%'}
              />
              <ResultRow
                label="Conviction-tier cap"
                value={result.convictionCapPct.toFixed(0) + '%'}
              />
              <ResultRow
                label="Final size (lower of the two)"
                value={result.recommendedPct.toFixed(2) + '%'}
                highlight
              />
            </div>

            {/* Warnings + connections */}
            <div
              style={{
                padding: '20px 24px',
                background: 'var(--sp-parchment-bg)',
                border: '1px solid var(--sp-divider)',
                borderRadius: 'var(--sp-radius-card)',
              }}
            >
              <div
                style={{
                  fontFamily: 'var(--sp-font-mono)',
                  fontSize: 10,
                  textTransform: 'uppercase',
                  letterSpacing: '0.16em',
                  color: 'var(--sp-aged-brass)',
                  marginBottom: 10,
                }}
              >
                Honest Read
              </div>
              <div style={{ fontSize: 13, lineHeight: 1.6, color: 'rgba(232, 221, 196, 0.78)' }}>
                {result.honestRead}
              </div>
            </div>

            {/* Linked dossiers */}
            <div style={{ marginTop: 16, fontSize: 12, color: 'rgba(232, 221, 196, 0.55)' }}>
              <span
                style={{
                  fontFamily: 'var(--sp-font-mono)',
                  fontSize: 10,
                  letterSpacing: '0.14em',
                  textTransform: 'uppercase',
                  color: 'rgba(232, 221, 196, 0.45)',
                  marginRight: 8,
                }}
              >
                Connected dossiers:
              </span>
              <Link
                href="/strategic-playbook/buffett-compound-certainty"
                style={{ color: 'var(--sp-aged-brass)' }}
              >
                Buffett
              </Link>
              {' · '}
              <Link
                href="/strategic-playbook/ackman-conviction-platform"
                style={{ color: 'var(--sp-aged-brass)' }}
              >
                Ackman
              </Link>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @media (max-width: 900px) {
          .position-sizer-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
}

// ─── Calculation logic ─────────────────────────────
function calculateSize({
  accountSize,
  winProb,
  winPct,
  lossPct,
  convictionTier,
  kellyFraction,
}) {
  // Kelly = (bp - q) / b  where b = win/loss ratio, p = win prob, q = 1-p
  const winLossRatio = lossPct > 0 ? winPct / lossPct : 0;
  const lossProb = 1 - winProb;
  const fullKelly =
    winLossRatio > 0 ? (winProb * winLossRatio - lossProb) / winLossRatio : 0;

  const fractionalKelly = fullKelly * kellyFraction;

  // Conviction tier cap
  const tier = CONVICTION_TIERS.find((t) => t.id === convictionTier);
  const convictionCapFraction = tier ? tier.pct / 100 : 0.05;

  // Take the LOWER of fractional Kelly and conviction cap (defensive)
  // If Kelly is negative, the bet shouldn't be taken — but show 0 size
  const recommendedFraction =
    fullKelly <= 0 ? 0 : Math.max(0, Math.min(fractionalKelly, convictionCapFraction));

  const recommendedSize = Math.round(accountSize * recommendedFraction);

  // Expected value per dollar
  const evPerDollarRaw = winProb * winPct - lossProb * lossPct;
  const evPerDollar =
    (evPerDollarRaw >= 0 ? '+' : '') +
    (evPerDollarRaw * 100).toFixed(2) +
    '¢';

  // Verdict
  let verdict = '';
  let honestRead = '';
  if (fullKelly <= 0) {
    verdict = 'Kelly says do NOT take this bet. The math is negative.';
    honestRead =
      'Negative expected value — at these win probability and win/loss ratios, you lose money over time. Either your edge estimate is too low, your loss estimate too high, or both.';
  } else if (recommendedFraction === convictionCapFraction) {
    verdict = `Conviction tier (${tier.label}) caps you at ${tier.pct}% of account. Kelly would let you go larger.`;
    honestRead = `Kelly's full size is ${(fullKelly * 100).toFixed(2)}% but your conviction tier caps at ${tier.pct}%. The cap is the right call — Kelly assumes you know probabilities precisely; conviction tier protects you from overconfident estimates.`;
  } else {
    verdict = `Asymmetric within your account. Size at $${recommendedSize.toLocaleString()} (${(recommendedFraction * 100).toFixed(2)}% of account).`;
    honestRead = `Half-Kelly is recommended for most. If your edge estimate is uncertain, drop to quarter-Kelly. Position size is one decision; exit criteria (when you'd close at loss) is the other half.`;
  }

  return {
    recommendedSize,
    recommendedPct: recommendedFraction * 100,
    kellyPct: fullKelly * 100,
    fractionalKellyPct: fractionalKelly * 100,
    convictionCapPct: convictionCapFraction * 100,
    evPerDollar,
    verdict,
    honestRead,
  };
}

// ─── UI primitives ─────────────────────────────────
function Field({ label, suffix, hint, children }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div
        style={{
          fontFamily: 'var(--sp-font-mono)',
          fontSize: 10,
          textTransform: 'uppercase',
          letterSpacing: '0.14em',
          color: 'rgba(232, 221, 196, 0.55)',
          marginBottom: 6,
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <span>{label}</span>
        {suffix && <span style={{ color: 'var(--sp-aged-brass)' }}>{suffix}</span>}
      </div>
      {children}
      {hint && (
        <div
          style={{
            fontSize: 11,
            color: 'rgba(232, 221, 196, 0.50)',
            marginTop: 4,
            lineHeight: 1.5,
          }}
        >
          {hint}
        </div>
      )}
    </div>
  );
}

const inputStyle = {
  width: '100%',
  padding: '10px 14px',
  background: 'var(--sp-charcoal-black)',
  border: '1px solid var(--sp-divider)',
  borderRadius: 'var(--sp-radius)',
  color: 'var(--sp-warm-cream)',
  fontFamily: 'var(--sp-font-body)',
  fontSize: 14,
  outline: 'none',
};

function NumberInput({ value, onChange, step, min, max }) {
  return (
    <input
      type="number"
      value={value}
      step={step}
      min={min}
      max={max}
      onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
      style={inputStyle}
    />
  );
}

function ResultRow({ label, value, highlight }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '8px 0',
        borderBottom: '1px solid rgba(58, 47, 40, 0.5)',
      }}
    >
      <span
        style={{
          fontSize: 12,
          color: highlight ? 'var(--sp-warm-cream)' : 'rgba(232, 221, 196, 0.65)',
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontFamily: 'var(--sp-font-mono)',
          fontSize: 13,
          color: highlight ? 'var(--sp-aged-brass)' : 'var(--sp-warm-cream)',
          fontWeight: highlight ? 600 : 400,
        }}
      >
        {value}
      </span>
    </div>
  );
}

function ToolTopbar() {
  return (
    <div className="sp-topbar">
      <div>
        <h1>Strategic Intelligence</h1>
        <div className="sp-topbar-meta">Tools · Position Sizer</div>
      </div>
      <nav className="sp-topbar-nav">
        <Link href="/strategic-playbook">Dashboard</Link>
        <Link href="/strategic-playbook/library">Library</Link>
        <Link href="/strategic-playbook/tools" className="sp-active">
          Tools
        </Link>
        <Link href="/strategic-playbook/decisions">Decisions</Link>
        <Link href="/strategic-playbook/simulate">Simulate</Link>
      </nav>
    </div>
  );
}
